import { useEffect, useRef } from 'react';
import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import type { ImportSession } from '@timetable/types';

const STEP_LABELS: Record<number, string> = {
  1: 'Academic Year',
  2: 'Master Data',
  3: 'Include Timetable?',
  4: 'Timetable Format',
  5: 'Upload Timetable',
  6: 'Confirm & Import',
};

interface SessionLockedScreenProps {
  session: ImportSession;
  lockHolderName?: string;
  onLockExpired: () => void;
}

export function SessionLockedScreen({
  session,
  lockHolderName,
  onLockExpired,
}: SessionLockedScreenProps) {
  const stepLabel = STEP_LABELS[session.current_step] ?? `Step ${session.current_step}`;
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    // Poll every 60 s to detect lock expiry (15 min threshold)
    intervalRef.current = setInterval(() => {
      if (!session.locked_at) return;
      const ageMs = Date.now() - new Date(session.locked_at).getTime();
      if (ageMs > 15 * 60 * 1000) {
        onLockExpired();
      }
    }, 60_000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [session.locked_at, onLockExpired]);

  return (
    <Box sx={{ py: 4, textAlign: 'center' }}>
      <LockIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
      <Typography variant="h6" gutterBottom>
        Import session is locked
      </Typography>
      <Alert severity="warning" sx={{ mb: 3, textAlign: 'left' }}>
        {lockHolderName
          ? `${lockHolderName} is currently editing this import session.`
          : 'Another admin is currently editing this import session.'}{' '}
        The lock will expire automatically after 15 minutes of inactivity.
      </Alert>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Current step: {stepLabel}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mt: 2 }}>
        <CircularProgress size={16} />
        <Typography variant="caption" color="text.secondary">
          Checking for lock expiry every 60 seconds…
        </Typography>
      </Box>
    </Box>
  );
}
